// The board, as data: which column a card is in, what moving it writes, and the
// sentence each write says afterwards.
//
// Pure - no React, no browser, no I/O - the same split `day/day-model.ts` and
// `shell/screens.ts` keep, so every rule below can be read and tested on its own.
//
// TWO THINGS THIS FILE DELIBERATELY DOES NOT KNOW:
//
//   * WHAT THE COLUMNS ARE. Their keys, their words and the status each one
//     writes all arrive in the payload (`GET /board`), for the same reason the
//     day payload carries `schedules.cap` and `meta`: two clients must not be
//     able to phrase or place one thing two ways, and a fifth column must not
//     mean editing a client. Nothing here writes "In Review" or knows that
//     To-do is a null.
//   * HOW TO FINISH SOMETHING. The completing column is the one the payload
//     marks `completes`, and landing a card in it is the completion write the
//     day screen already makes - the same table, the same unique index, the same
//     undo. `movePlan` returns that as a step; it does not invent a second way.

import type { BoardCard, BoardColumn, BoardResponse, BoardStatus, IsoDay } from '../api/client.ts';
import { dayAndMonth } from '../iso-day.ts';

/** One write a move has to make, in the order they have to be made. */
export type MoveStep =
  | { op: 'complete'; day: IsoDay }
  | { op: 'uncomplete'; day: IsoDay }
  | { op: 'place'; status: BoardStatus | null };

export interface CardAt {
  card: BoardCard;
  column: BoardColumn;
}

/** Where a card is right now, or null when the board no longer holds it. */
export function findCard(board: BoardResponse, cardId: string): CardAt | null {
  for (const column of board.columns) {
    const card = column.cards.find((c) => c.id === cardId);
    if (card !== undefined) return { card, column };
  }
  return null;
}

export function columnAt(board: BoardResponse, key: string): BoardColumn | null {
  return board.columns.find((column) => column.key === key) ?? null;
}

/**
 * The column `step` places to the side of this one, or null at either end.
 *
 * This is the keyboard's half of drag-and-drop, and it is not optional: a board
 * that can only be rearranged by dragging is a board a keyboard, a screen reader
 * and a switch cannot use at all. Every move a drag can make, a pair of buttons
 * can make too.
 */
export function neighbour(board: BoardResponse, key: string, step: -1 | 1): BoardColumn | null {
  const at = board.columns.findIndex((column) => column.key === key);
  if (at < 0) return null;
  return board.columns[at + step] ?? null;
}

/**
 * Every write moving this card from one column to another has to make.
 *
 * The four cases, and why each is what it is:
 *
 *   nowhere to go     the same column - no writes, so a drop back where it
 *                     started is not a request and not a line on the toast.
 *   into Complete     ONE completion, for the server's today, and the status is
 *                     left exactly as it was. That is what makes the undo whole:
 *                     deleting the completion again puts the card back in the
 *                     column it was dragged out of rather than in To-do.
 *   out of Complete   the completion is deleted FOR THE DAY IT CARRIES, not for
 *                     today - a card finished last Tuesday is untickd by naming
 *                     Tuesday - and then the destination's status is written,
 *                     because the client cannot see the status hiding under a
 *                     finished card and must not guess at it.
 *   anywhere else     one status, which is the destination's own, sent back
 *                     exactly as the payload gave it.
 */
export function movePlan(at: CardAt, to: BoardColumn, today: IsoDay): MoveStep[] {
  if (at.column.key === to.key) return [];

  if (to.completes) return [{ op: 'complete', day: today }];

  const leaving: MoveStep[] =
    at.column.completes && at.card.completedDay !== null
      ? [{ op: 'uncomplete', day: at.card.completedDay }]
      : [];

  return [...leaving, { op: 'place', status: to.status }];
}

/**
 * The board with a card drawn in a different column, before the server has
 * agreed.
 *
 * The day screen makes the same trade for a tick (`useDayScreen`): a drag that
 * waited for a round trip before the card moved would feel broken, so the screen
 * moves first and puts the card back if the write does not land.
 *
 * `completedDay` is kept in step here as well as the column, because that field
 * is what the card draws and what an un-complete has to name later.
 */
export function withCardMoved(board: BoardResponse, cardId: string, toKey: string): BoardResponse {
  const at = findCard(board, cardId);
  const to = columnAt(board, toKey);
  if (at === null || to === null || at.column.key === to.key) return board;

  const moved: BoardCard = { ...at.card, completedDay: to.completes ? board.today : null };

  return {
    ...board,
    columns: board.columns.map((column) => {
      if (column.key === at.column.key) {
        return {
          ...column,
          total: Math.max(0, column.total - 1),
          cards: column.cards.filter((c) => c.id !== cardId),
        };
      }
      if (column.key === to.key) {
        return {
          ...column,
          total: column.total + 1,
          // A completion written now is the newest one. Open columns retain the
          // reminder due-day order they use everywhere else.
          cards: to.completes
            ? [moved, ...column.cards].slice(0, Math.max(1, column.cards.length))
            : insertByDue(column.cards, moved),
        };
      }
      return column;
    }),
  };
}

/**
 * Where a card lands in a column it has just been moved into.
 *
 * Worst-overdue first, then dated, then undated - the order the server sends
 * every column in (`byReminderOrder`, api/src/domain/day.ts), applied here only
 * so a moved card does not visibly jump when the board is next read. Among cards
 * due on the same day it goes last, which is where an existing card belongs
 * against the ones already there.
 */
function insertByDue(cards: readonly BoardCard[], card: BoardCard): BoardCard[] {
  const before = cards.findIndex(
    (other) => card.dueDay !== null && (other.dueDay === null || card.dueDay < other.dueDay),
  );
  const next = [...cards];
  next.splice(before < 0 ? next.length : before, 0, card);
  return next;
}

/** The board with a card put back where it was, after a write did not land. */
export function withCardRestored(
  board: BoardResponse,
  card: BoardCard,
  toKey: string,
): BoardResponse {
  const current = findCard(board, card.id);
  if (current === null || current.column.key === toKey) return board;

  return {
    ...board,
    columns: board.columns.map((column) => {
      const without = column.cards.filter((c) => c.id !== card.id);
      if (column.key === current.column.key) {
        return { ...column, total: Math.max(0, column.total - 1), cards: without };
      }
      if (column.key === toKey) {
        return {
          ...column,
          total: column.total + 1,
          cards: column.completes ? [card, ...without] : insertByDue(without, card),
        };
      }
      return { ...column, cards: without };
    }),
  };
}

/** Append exactly one older Complete page, without replacing the working board. */
export function withOlderCompletePage(board: BoardResponse, page: BoardResponse): BoardResponse {
  const incoming = page.columns.find((column) => column.completes);
  if (incoming === undefined) return board;

  return {
    ...board,
    columns: board.columns.map((column) => {
      if (!column.completes) return column;
      const seen = new Set(column.cards.map((card) => card.id));
      const older = incoming.cards.filter((card) => !seen.has(card.id));
      return {
        ...column,
        // Keep the count from the board that asked. A completion move can land
        // while this read is in flight and has already adjusted it locally; the
        // older page is authoritative about its rows and next position, not
        // about rewinding that newer board snapshot.
        total: column.total,
        cards: [...column.cards, ...older],
        nextCursor: incoming.nextCursor,
      };
    }),
  };
}

/**
 * The line under a card's title: when it is due, or when it was finished.
 *
 * A DATE IN WORDS IS PHRASED HERE, and a RULE never is. The day screen's `meta`
 * comes from the server because "every 2 weeks" is genuinely ambiguous and two
 * clients would word it two ways (AGENTS.md); a calendar date is not, and this
 * client already phrases one - `dayAndMonth` heads the day screen and writes
 * every "it is not due on 12 August" line the editor says. The day the card is
 * measured AGAINST is still the server's: `today` arrives in the payload, and
 * nothing here asks the machine what day it is.
 *
 * Overdue is words and weight, never a colour and never a count against you
 * (prototype-v2.md section 1.4): a reminder that has gone past its date is late,
 * not a failure, and this product does not nag.
 */
export function cardMeta(card: BoardCard, today: IsoDay): string {
  if (card.completedDay !== null) {
    return card.completedDay === today
      ? 'Finished today'
      : `Finished ${dayAndMonth(card.completedDay)}`;
  }
  if (card.dueDay === null) return '';
  if (card.dueDay < today) return `Was due ${dayAndMonth(card.dueDay)}`;
  if (card.dueDay === today) return 'Due today';
  return `Due ${dayAndMonth(card.dueDay)}`;
}

/** Whether a card's due day has gone by, which the meta line says in words. */
export function isOverdue(card: BoardCard, today: IsoDay): boolean {
  return card.completedDay === null && card.dueDay !== null && card.dueDay < today;
}

/**
 * The line under an archived card's title, in the Archived list.
 *
 * It deliberately does NOT say when the card was due. An archived card is off
 * the board and nothing about it is outstanding, so "Due today" under a row you
 * cannot act on is an obligation the screen is inventing - and "Was due 15
 * August" is worse, because it reads as a thing still owed. What is worth saying
 * is that a finished one was finished, which "Archived" on its own does not
 * carry. The wording is the schedules list's in Settings, for the same rows.
 */
export function archivedLine(card: BoardCard, today: IsoDay): string {
  const meta = card.completedDay === null ? 'Archived' : cardMeta(card, today);
  return `${meta} - history kept`;
}

/**
 * The line under the board's heading, which is the screen saying what to do.
 *
 * Three states, because a board with nothing in any column is a screen with no
 * cards to drag and telling somebody to drag one is telling them to use
 * something that is not there - the trap this client has been caught by five
 * times (web/src/README.md). Both empty lines name **Add a card**, which is at
 * the foot of every column and therefore on screen at every width; the middle
 * one also names Archived, which is the other way a card gets back on the board
 * and is right there below it.
 */
export function boardLine(board: BoardResponse): string {
  const hasCards = board.columns.some((column) => column.total > 0);
  if (hasCards) {
    return 'Drag a card between columns, or use the arrows on it. A card in Complete is ticked off for good - the same tick as on your day.';
  }
  if (board.archived.length > 0) {
    return 'Nothing on your board right now. Start a card with Add a card, at the foot of any column below, or put one back from Archived.';
  }
  return 'Nothing on your board yet. A card is a one-off thing to do: it does not repeat, and once it reaches Complete it is finished for good. Start one with Add a card, at the foot of any column below.';
}

// ------------------------------------------------------------ sentences -----
//
// EVERY SUCCESSFUL WRITE NAMES WHAT IT ACTED ON, and any write whose result can
// land somewhere the person is not looking says where (web/src/README.md - the
// same defect was found four times in five review rounds). On a board that is
// not a hypothetical: the columns scroll sideways on a phone, so the column a
// card was just moved to is routinely off screen when it gets there.

export function movedNotice(title: string, column: BoardColumn): string {
  return `"${title}" moved to ${column.label}.`;
}

export function addedNotice(title: string, column: BoardColumn): string {
  return `"${title}" added to ${column.label}.`;
}

export function archivedNotice(title: string): string {
  return `"${title}" archived. It is off the board and under Archived below it.`;
}

/**
 * Deliberately does NOT name a column.
 *
 * A restored card goes back to the column it was archived from, and only the
 * server knows which that is - an archived card is on no column to be asked, and
 * its `status` is not something this screen may read and translate into a column
 * itself: that mapping belongs to the payload (see the head of this file). The
 * card reappearing on the board is what says where it went, and this says which
 * card it was.
 */
export function restoredNotice(title: string): string {
  return `"${title}" is back on your board.`;
}

/** Named for the card it acts on, because "Move right" alone says which button
 *  exists and not which card it moves - several cards make several of them. */
export function moveLabel(title: string, column: BoardColumn): string {
  return `Move "${title}" to ${column.label}`;
}
